import React, { useState } from 'react';
import { Form, Input, message } from 'antd';
import { CreditCard, Shield, AlertTriangle, HelpCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import api from '../api/client';
import clsx from 'clsx';

const PaymentSystemCard = ({ title, description, icon: Icon, isActive, onClick, colorClass }) => (
  <button
    onClick={onClick}
    className={clsx(
      "w-full text-left p-6 rounded-xl border transition-all duration-300 flex items-start gap-4 group",
      isActive 
        ? "bg-primary/10 border-primary/50 shadow-neon" 
        : "bg-surface hover:bg-surface/80 border-white/5 hover:border-white/10"
    )}
  >
    <div className={clsx("p-3 rounded-lg text-white shadow-lg flex-shrink-0 transition-transform group-hover:scale-110", colorClass)}>
      <Icon size={24} />
    </div>
    <div>
      <h3 className={clsx("font-bold text-lg mb-1 transition-colors", isActive ? "text-white" : "text-slate-300 group-hover:text-white")}>
        {title}
      </h3>
      <p className="text-sm text-slate-400 leading-relaxed">
        {description}
      </p>
    </div>
  </button>
);

export default function ServicePayments() {
  const [form] = Form.useForm();
  const [activeSystem, setActiveSystem] = useState('prodamus_service');
  const [loading, setLoading] = useState(false);

  const handleSavePayment = async (values) => {
    setLoading(true);
    try {
      await api.post('/admin/payments', {
        payment_system: activeSystem,
        credentials: values,
      });
      message.success('Настройки платежной системы сохранены');
    } catch (err) {
      message.error('Ошибка при сохранении');
    } finally {
      setLoading(false);
    }
  };

  const renderFormFields = () => {
    switch(activeSystem) {
      case 'prodamus_service':
        return (
          <>
            <Form.Item 
              label={<span className="text-slate-300">API Ключ (для сервиса)</span>}
              name="apiKey"
              rules={[{ required: true, message: 'Введите API ключ' }]}
            >
              <Input.Password className="input-field" placeholder="Ключ от Prodamus ДЛЯ НАШЕГО СЕРВИСА" />
            </Form.Item>
            <Form.Item 
              label={<span className="text-slate-300">Merchant ID</span>}
              name="merchantId"
            >
              <Input className="input-field" placeholder="Ваш Merchant ID" />
            </Form.Item>
          </>
        );
      case 'cloudpayments_service':
        return (
          <>
            <Form.Item 
              label={<span className="text-slate-300">Public ID</span>}
              name="publicId"
              rules={[{ required: true }]}
            >
              <Input className="input-field" placeholder="Public ID для сервиса" />
            </Form.Item>
            <Form.Item 
              label={<span className="text-slate-300">API Secret</span>}
              name="apiSecret"
              rules={[{ required: true }]}
            >
              <Input.Password className="input-field" placeholder="API Secret для сервиса" />
            </Form.Item>
          </>
        );
      case 'stripe_service':
        return (
          <>
            <Form.Item 
              label={<span className="text-slate-300">Secret Key</span>}
              name="secretKey"
              rules={[{ required: true }]}
            >
              <Input.Password className="input-field" placeholder="sk_live_..." />
            </Form.Item>
            <Form.Item 
              label={<span className="text-slate-300">Publishable Key</span>}
              name="publishableKey"
              rules={[{ required: true }]}
            >
              <Input className="input-field" placeholder="pk_live_..." />
            </Form.Item>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row justify-between items-start gap-8 mb-12">
        <div className="flex-1">
          <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-slate-400 mb-4">
            Биллинг сервиса
          </h1>
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-6 flex items-start gap-4">
            <AlertTriangle className="text-amber-500 flex-shrink-0 mt-1" size={24} />
            <div>
              <h3 className="text-amber-500 font-bold mb-2">Административный раздел</h3>
              <p className="text-slate-300 text-sm leading-relaxed">
                Здесь настраиваются платежные шлюзы, через которые <strong>пользователи оплачивают подписку на ваш сервис аналитики</strong>. 
                Не путайте с разделом "Интеграции", где пользователи подключают свои платежные системы.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="space-y-4">
          <PaymentSystemCard 
            title="Prodamus"
            description="Основной шлюз для приема платежей в РФ (карты, СБП)."
            icon={CreditCard}
            colorClass="bg-gradient-to-br from-purple-500 to-indigo-600"
            isActive={activeSystem === 'prodamus_service'}
            onClick={() => setActiveSystem('prodamus_service')}
          />
          <PaymentSystemCard 
            title="CloudPayments"
            description="Резервный эквайринг для высокой надежности."
            icon={Shield}
            colorClass="bg-gradient-to-br from-blue-500 to-cyan-500"
            isActive={activeSystem === 'cloudpayments_service'}
            onClick={() => setActiveSystem('cloudpayments_service')}
          />
          <PaymentSystemCard 
            title="Stripe"
            description="Прием международных платежей в валюте."
            icon={CreditCard}
            colorClass="bg-gradient-to-br from-indigo-500 to-violet-600"
            isActive={activeSystem === 'stripe_service'}
            onClick={() => setActiveSystem('stripe_service')}
          />
        </div>

        <div className="lg:col-span-2">
          <motion.div 
            key={activeSystem}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="glass-panel p-8"
          >
            <div className="flex items-center gap-3 mb-8 pb-8 border-b border-white/5">
              <div className="p-2 bg-primary/10 rounded-lg text-primary">
                <CreditCard size={24} />
              </div>
              <h2 className="text-2xl font-bold text-white">
                Настройка {activeSystem.split('_')[0].charAt(0).toUpperCase() + activeSystem.split('_')[0].slice(1)}
              </h2>
            </div>

            <Form
              form={form}
              layout="vertical"
              onFinish={handleSavePayment}
              className="space-y-6"
            >
              {renderFormFields()}

              <div className="pt-4 flex justify-end">
                <button 
                  type="submit"
                  disabled={loading}
                  className="btn-primary min-w-[200px] flex justify-center"
                >
                  {loading ? 'Сохранение...' : 'Сохранить настройки'}
                </button>
              </div>
            </Form>

            <div className="mt-8 p-4 rounded-lg bg-surface/50 border border-white/5 text-sm text-slate-400 flex gap-3">
              <HelpCircle className="flex-shrink-0 text-slate-500" size={20} />
              <p>
                Убедитесь, что вы используете ключи от аккаунта компании, а не личного кабинета. 
                Все платежи будут поступать на счет, привязанный к этим ключам.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
